import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import { useNavigate } from "react-router-dom"; 
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Spinner,
  Text,
  useToast,
  VStack,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  ListItem,
  UnorderedList,
  Image,
  Container,
  Center,
  Heading,
  Radio,
  RadioGroup,
  Stack,
  Icon,
  Badge,
  AspectRatio,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { CheckCircleIcon, ViewIcon, EditIcon, DeleteIcon, DownloadIcon } from "@chakra-ui/icons";
import SignaturePad from "react-signature-canvas";
import { motion } from "framer-motion";
import moment from "moment";
import { FaFileAlt, FaSignature, FaUser, FaCalendarAlt, FaInfoCircle } from "react-icons/fa";
import { hasValidRegistrationSignature } from "./utils/signatureHelpers";
import {
  buildPortalDocumentsList,
  resolveProgramForRegistration,
  countCompletedPortalDocs,
} from "./utils/programDocuments";





// PaymentMethodSelection Component
const PaymentMethodSelection = ({ 
  registrationData, 
  registrationID, 
  onSkillsFutureSelect, 
  onCreditCardSelect,
  paymentBalance 
}) => {
  const [selectedMethod, setSelectedMethod] = useState('');
  const [processing, setProcessing] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [showBackdoor, setShowBackdoor] = useState(false);
  const toast = useToast();

  // Calculate SkillsFuture eligibility
  const getSkillsFutureEligibility = () => {
    if (!registrationData?.start_date) return null;
    
    const startDate = moment(registrationData.start_date);
    const today = moment();
    const daysUntilEligible = startDate.diff(today, 'days');
    
    // Backdoor override for testing
    if (showBackdoor) {
      return {
        canPayNow: true,
        daysLeft: 0,
        daysUntilCourse: daysUntilEligible,
        skillsFutureAvailableDate: startDate.subtract(60, 'days')
      };
    }
    
    return {
      canPayNow: daysUntilEligible <= 60,
      daysLeft: daysUntilEligible > 60 ? daysUntilEligible - 60 : 0,
      daysUntilCourse: daysUntilEligible,
      skillsFutureAvailableDate: startDate.subtract(60, 'days')
    };
  };

  const eligibility = getSkillsFutureEligibility();

  // Handle title click for backdoor
  const handleTitleClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    if (newCount === 5) {
      setShowBackdoor(!showBackdoor);
      setClickCount(0);
      toast({
        title: showBackdoor ? "Backdoor Deactivated" : "Backdoor Activated",
        description: showBackdoor ? "Showing normal SkillsFuture eligibility" : "Showing SkillsFuture as available",
        status: "info",
        duration: 2000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const handleMethodSelect = (value) => {
    // Prevent selection if SkillsFuture is not eligible
    if (value === 'skillsfuture' && !eligibility?.canPayNow) {
      toast({
        title: "SkillsFuture Not Available",
        description: `SkillsFuture payment will be available in ${eligibility.daysLeft} days on ${eligibility.skillsFutureAvailableDate.format("DD MMM YYYY (ddd)")}`,
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    
    // Prevent selection if SkillsFuture has already been claimed
    if (value === 'skillsfuture' && registrationData?.skillsfuture_claimed_amount !== null) {
      toast({
        title: "SkillsFuture Already Claimed",
        description: `You have already claimed SGD $${registrationData.skillsfuture_claimed_amount} from SkillsFuture for this course.`,
        status: "info",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    
    setSelectedMethod(value);
  };

  const handleProceed = async () => {
    if (!selectedMethod) {
      toast({
        title: "Error",
        description: "Please select a payment method before proceeding.",
        status: "error",
        isClosable: true,
        position: "top",
      });
      return;
    }

    setProcessing(true);
    
    if (selectedMethod === 'skillsfuture') {
      await onSkillsFutureSelect();
    } else if (selectedMethod === 'creditcard') {
      await onCreditCardSelect();
    }
    
    setProcessing(false);
  };

  return (
    <Container 
      w={"100%"} 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      bg="gray.50" 
      minH="100vh" 
      py={8}
    >
      <Box 
        width="100%" 
        bg="white" 
        maxW={"800px"} 
        px={10} 
        py={10} 
        borderRadius="lg" 
        boxShadow="md"
      >
        <Heading 
          size={"sm"}
          mb={8} 
          color="brand.700" 
          borderBottom="2px solid" 
          borderColor="brand.400" 
          pb={2} 
          width="fit-content"
          onClick={handleTitleClick}
          userSelect="none"
        >
          Payment Method Selection
        </Heading>

        <Alert status="info" mb={8}>
          <AlertIcon />
          Please select your preferred payment method to complete your enrollment.
        </Alert>

        <VStack spacing={8} align="stretch">
          {/* Payment Balance Display */}
          <Box 
            p={6} 
            border="2px solid" 
            borderColor="brand.300" 
            borderRadius="lg"
            bg="gray.50"
          >
            <Text fontSize="lg" fontWeight="bold" color="brand.700" mb={2}>
              Outstanding Payment Balance
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="teal.500">
              SGD ${paymentBalance}
            </Text>
            <Text fontSize="sm" mt={2}>
              If you’ve already made a payment, please wait while we verify it.
            </Text>
            <Text fontSize="sm" mt={2}>
              The page will update automatically once your payment is confirmed.
            </Text>
            <Text fontSize="sm" mt={2}>
              Otherwise, you may proceed to make a payment below.
            </Text>
          </Box>

          {/* Payment Method Selection */}
          <Box>
            <Text fontSize="lg" fontWeight="bold" color="brand.700" mb={4}>
              Select Payment Method
            </Text>
            
            <RadioGroup value={selectedMethod} onChange={handleMethodSelect}>
              <Stack spacing={4}>
                <Box 
                  p={6} 
                  border="2px solid" 
                  borderColor={
                    selectedMethod === 'skillsfuture' ? 'orange.500' : 
                    registrationData?.skillsfuture_claimed_amount !== null ? 'green.600' :
                    'brand.300'
                  } 
                  borderRadius="lg"
                  bg={
                    selectedMethod === 'skillsfuture' ? 'orange.50' : 
                    registrationData?.skillsfuture_claimed_amount !== null ? 'green.50' :
                    eligibility?.canPayNow ? 'white' : 'brand.50'
                  }
                  cursor={
                    registrationData?.skillsfuture_claimed_amount !== null ? "not-allowed" :
                    eligibility?.canPayNow ? "pointer" : "not-allowed"
                  }
                  onClick={() => handleMethodSelect('skillsfuture')}
                  _hover={
                    registrationData?.skillsfuture_claimed_amount !== null ? {} :
                    eligibility?.canPayNow ? { borderColor: 'orange.500' } : {}
                  }
                  opacity={
                    registrationData?.skillsfuture_claimed_amount !== null ? 1 :
                    eligibility?.canPayNow ? 1 : 1.0
                  }
                >
                  <Radio 
                    value="skillsfuture" 
                    colorScheme="orange"
                    isDisabled={!eligibility?.canPayNow || registrationData?.skillsfuture_claimed_amount !== null}
                  >
                    <VStack align="start" spacing={2} ml={2}>
                      <HStack justify="space-between" width="100%">
                        <Text fontWeight="bold" color={
                          registrationData?.skillsfuture_claimed_amount !== null ? 'green.600' :
                          eligibility?.canPayNow ? 'brand.700' : 'brand.500'
                        }>
                          SkillsFuture Credit
                        </Text>
                        {registrationData?.skillsfuture_claimed_amount !== null ? (
                          <Text 
                            fontSize="xs" 
                            fontWeight="600"
                            color="green.600"
                          >
                            ✅ Already Claimed: SGD ${registrationData.skillsfuture_claimed_amount}
                          </Text>
                        ) : (
                          eligibility && (
                            <Text 
                              fontSize="xs" 
                              fontWeight="600"
                              color={eligibility.canPayNow ? 'green.600' : 'orange.500'}
                            >
                              {eligibility.canPayNow 
                                ? `✅ Available (${eligibility.daysUntilCourse} days to course)`
                                : `⏳ In ${eligibility.daysLeft} days on ${eligibility.skillsFutureAvailableDate.format("DD MMM (ddd)")}`
                              }
                            </Text>
                          )
                        )}
                      </HStack>
                      <Text fontSize="sm" color={
                        registrationData?.skillsfuture_claimed_amount !== null ? 'green.600' :
                        eligibility?.canPayNow ? 'brand.500' : 'brand.500'
                      }>
                        {registrationData?.skillsfuture_claimed_amount !== null ? 
                          "You have already used your SkillsFuture credits for this course. The claimed amount has been applied to your payment balance." :
                          "Use your SkillsFuture credits to pay for this course. You will be redirected to the SkillsFuture portal to complete the payment."
                        }
                      </Text>
                      {!eligibility?.canPayNow && registrationData?.skillsfuture_claimed_amount === null && (
                        <Text fontSize="sm" fontWeight="600" color="orange.500">
                          Note: SkillsFuture payment is only available within 60 days of course start date.
                        </Text>
                      )}
                    </VStack>
                  </Radio>
                </Box>

                <Box 
                  p={6} 
                  border="2px solid" 
                  borderColor={selectedMethod === 'creditcard' ? 'orange.500' : 'brand.300'} 
                  borderRadius="lg"
                  bg={selectedMethod === 'creditcard' ? 'orange.50' : 'white'}
                  cursor="pointer"
                  onClick={() => setSelectedMethod('creditcard')}
                  _hover={{ borderColor: 'orange.500' }}
                >
                  <Radio value="creditcard" colorScheme="orange">
                    <VStack align="start" spacing={2} ml={2}>
                      <Text fontWeight="bold" color="brand.700">
                        Credit Card / PayNow
                      </Text>
                      <Text fontSize="sm" color="brand.500">
                        Pay directly with your credit card or PayNow. 
                        You will be redirected to our secure payment gateway.
                      </Text>
                    </VStack>
                  </Radio>
                </Box>
              </Stack>
            </RadioGroup>
          </Box>

          {/* Proceed Button */}
          <Button
            bg="brand.600"
            color="white"
            size="lg"
            onClick={handleProceed}
            isLoading={processing}
            loadingText="Processing..."
            isDisabled={!selectedMethod}
            _hover={{ bg: 'brand.700' }}
            _active={{ bg: 'brand.800' }}
          >
            Proceed with Payment
          </Button>
        </VStack>
      </Box>
    </Container>
  );
};

export const Enrollment = () => {
  const { registrationID } = useParams();
  const location = useLocation();
  const [registrationData, setRegistrationData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const sigPad = useRef(null);
  const toast = useToast();
  const navigate = useNavigate();
  const [isSignatureEmpty, setIsSignatureEmpty] = useState(true);
  const [showPaymentSelection, setShowPaymentSelection] = useState(false);
  const [showSkillsFutureForm, setShowSkillsFutureForm] = useState(false);
  const [claimRequest, setClaimRequest] = useState("");
  const [skillsFutureStatus, setSkillsFutureStatus] = useState(null);
  const [showSkillsFutureResult, setShowSkillsFutureResult] = useState(false);
  const [skillsFutureResult, setSkillsFutureResult] = useState(null);
  const [paymentLink, setPaymentLink] = useState(null);
  const [skillsFutureSubmitting, setSkillsFutureSubmitting] = useState(false);
  const [showInlineSuccess, setShowInlineSuccess] = useState(false);
  const [showThankYouPage, setShowThankYouPage] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [activeDocument, setActiveDocument] = useState(null);
  const { isOpen: isDocOpen, onOpen: onDocOpen, onClose: onDocClose } = useDisclosure();
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [downloadedDocs, setDownloadedDocs] = useState(new Set());
  const [isPolling, setIsPolling] = useState(false);
  const [currentDocCount, setCurrentDocCount] = useState(0);
  const [orgGstSettings, setOrgGstSettings] = useState({ gst_enabled: false, gst_rate: 0 }); // Organization GST settings
  const pollIntervalRef = useRef(null);
  const hasTriggeredGenerationRef = useRef(false);

  console.log("Registration ID from URL:", registrationID);

  // Check for payment success in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId && sessionId !== 'failed') {
      setPaymentSuccess(true);
      toast({
        title: "Payment Received",
        description: "Your payment has been successfully processed. Please review the documents and sign below.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      
      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [location.search, toast]);

  // Fetch organization GST settings
  useEffect(() => {
    const fetchOrganizationGstSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('organization')
          .select('gst_enabled, gst_rate')
          .eq('status', 'active')
          .single();
        
        if (error) {
          console.error('Error fetching organization GST settings:', error);
          return;
        }
        
        if (data) {
          setOrgGstSettings({
            gst_enabled: data.gst_enabled || false,
            gst_rate: parseFloat(data.gst_rate) || 0
          });
        }
      } catch (err) {
        console.error('Error fetching organization GST settings:', err);
      }
    };
    
    fetchOrganizationGstSettings();
  }, []);

  // Handle SkillsFuture response from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const skillsFutureStatus = urlParams.get('skillsfuture');
    
    if (skillsFutureStatus) {
      if (skillsFutureStatus === 'success') {
        const claimedAmount = urlParams.get('claimed');
        const claimId = urlParams.get('claimId');
        
        setSkillsFutureResult({
          type: 'success',
          claimedAmount: parseFloat(claimedAmount || 0),
          claimId: claimId,
          message: `SkillsFuture claim successful! Amount claimed: SGD $${claimedAmount}`
        });
        setShowSkillsFutureResult(true);
        
        // Show success toast
        toast({
          title: "SkillsFuture Claim Successful",
          description: `Successfully claimed SGD $${claimedAmount} from SkillsFuture.`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
        
        // Refresh registration data to get updated payment balance and check for completion
        fetchRegistrationAndCheckCompletion();
        
      } else if (skillsFutureStatus === 'failed' || skillsFutureStatus === 'error') {
        const message = urlParams.get('message') || 'SkillsFuture claim was not successful';
        
        setSkillsFutureResult({
          type: 'error',
          message: message
        });
        setShowSkillsFutureResult(true);
        
        // Show error toast
        toast({
          title: "SkillsFuture Claim Failed",
          description: message,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
      
      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [toast]);

  // --- Document Template Helper Functions ---
  const formatCurrency = (amount) => {
    const safe = Number.isFinite(Number(amount)) ? Number(amount) : 0;
    return new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 2 }).format(safe);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB');
  };

  // Remove participant name suffix from document title for display only
  const getDisplayDocumentName = (title) => {
    if (!title) return '';
    // Remove patterns like "- John Doe", "-{{participant_name}}", "-{participant_name}", etc.
    // This removes anything after a dash followed by optional spaces
    return title.replace(/\s*-\s*.*$/, '').trim();
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return '';
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
    const diffTime = Math.abs(end - start);
    const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
    return diffMonths.toString();
  };

  const calculateInstallmentSchedule = (reg) => {
    const subtotal = (reg?.full_certification_runs?.full_certification?.cost || 0) - (reg?.full_certification_runs?.full_certification?.discount || 0);
    
    // Apply GST if enabled
    const gstRate = orgGstSettings.gst_enabled ? (orgGstSettings.gst_rate / 100) : 0;
    const gstAmount = subtotal * gstRate;
    const totalFees = subtotal + gstAmount;
    
    const numInstallments = parseInt(reg?.full_certification_runs?.full_certification?.installments) || 1;
    const startDate = new Date(reg?.full_certification_runs?.start_date);
    const schedule = [];
    
    // Determine header text based on GST status
    const gstHeaderText = orgGstSettings.gst_enabled 
      ? `Amount (incl. ${orgGstSettings.gst_rate}% GST)<br/>(S$)` 
      : 'Amount<br/>(S$)';
    
    if (!Number.isFinite(totalFees) || Number.isNaN(startDate.getTime()) || numInstallments <= 0) {
      return `<table>
        <tr>
          <th>Instalment Schedule</th>
          <th>${gstHeaderText}</th>
          <th>Date Due</th>
        </tr>
      </table>`;
    }

    const installmentAmount = totalFees / numInstallments;
    let tableRows = '';
    let invoiceData = [];
    for (let i = 0; i < numInstallments; i++) {
      const installmentDate = new Date(startDate);
      installmentDate.setMonth(installmentDate.getMonth() + i);
      tableRows += `
        <tr>
          <td>${i + 1}</td>
          <td>${formatCurrency(installmentAmount)}</td>
          <td>${formatDate(installmentDate)}</td>
        </tr>`;
      invoiceData.push({
        registration_id: registrationID,
        invoice_type: 'course',
        amount: installmentAmount,
        status: 'pending payment',
        invoice_date: installmentDate,
        due_date: moment(installmentDate).add(1, 'month').toISOString(),
      });
    }

    return {html:`<table>
      <tr>
        <th>Instalment Schedule</th>
        <th>${gstHeaderText}</th>
        <th>Date Due</th>
      </tr>
      ${tableRows}
    </table>`,
    data: invoiceData};
  };

  // --- Document Generator Logic (Matching StudentPackage.js) ---

  // Function to view document in modal
  const viewDocument = (doc) => {
    setActiveDocument(doc);
    onDocOpen();
  };

  // Fetch and prepare documents (TMS program_document_templates when configured)
  const fetchAndPrepareDocuments = async (showLoading = true) => {
    if (!registrationData) return;

    try {
      const program = await resolveProgramForRegistration(supabase, registrationData);
      const { documentsList, expectedCount, allowedTemplateIds } =
        await buildPortalDocumentsList(supabase, {
          registrationIds: [registrationID],
          programType: program?.programType || null,
          programCode: program?.programCode || null,
          // Enrollment page historically only showed Registration-section templates in legacy mode
          legacySections: ['Registration'],
        });

      const doneCount = documentsList.filter((d) => d.status === 'done').length;
      const notStartedCount = documentsList.filter((d) => d.status === 'not_started').length;

      setDocuments(documentsList);
      setCurrentDocCount(doneCount);

      if (expectedCount > 0 && doneCount >= expectedCount) {
        setIsPolling(false);
      } else {
        setIsPolling(true);
      }

      return {
        hasDocuments: doneCount > 0,
        needsGeneration: notStartedCount > 0,
        expectedCount,
        allowedTemplateIds,
        doneCount,
      };
    } catch (error) {
      console.error("Error preparing documents:", error);
      if (showLoading) {
        toast({
          title: "Error loading documents",
          description: "Could not load documents.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
      return false;
    }
  };

  // Check if document generation is complete
  const checkGenerationComplete = async () => {
    try {
      const program = await resolveProgramForRegistration(supabase, registrationData);
      const { expectedCount, allowedTemplateIds } = await buildPortalDocumentsList(supabase, {
        registrationIds: [registrationID],
        programType: program?.programType || null,
        programCode: program?.programCode || null,
        legacySections: ['Registration'],
      });

      const { data: generatedDocs } = await supabase
        .from('document_generator_docs')
        .select('id, template_id, pdf_file_id')
        .eq('registration_id', registrationID);

      const currentCount = countCompletedPortalDocs(generatedDocs, allowedTemplateIds);
      return expectedCount > 0 && currentCount >= expectedCount;
    } catch (error) {
      console.error("Error checking generation completion:", error);
      return false;
    }
  };

  // Trigger PDF generation exactly like StudentPackage.js
  const triggerPDFGenerationForAll = async () => {
    if (!registrationData || !registrationID) return false;

    try {
      // copy-google-doc resolves the default doc-gen Google account server-side
      const { data, error } = await supabase.functions.invoke('copy-google-doc', {
        body: {
          registrationIds: [registrationID],
        },
      });

      if (error) {
        console.error('Error triggering PDF generation:', error);
        return false;
      } else {
        return true;
      }
    } catch (error) {
      console.error('Error in triggerPDFGenerationForAll:', error);
      return false;
    }
  };

  // Function to download document as PDF
  const downloadDocumentAsPdf = async (doc) => {
    setDownloadingDoc(doc.title);
    try {
      if (doc.isPdf && doc.pdfDownloadUrl) {
        const link = document.createElement('a');
        link.href = doc.pdfDownloadUrl;
        link.download = `${doc.title}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setDownloadedDocs(prev => new Set([...prev, doc.id || doc.title]));
        
        toast({
          title: "Download Started",
          description: `Downloading ${doc.title}...`,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast({
        title: "Download Error",
        description: "Failed to download PDF. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setDownloadingDoc(null);
    }
  };

  // Document polling effect exactly like StudentPackage.js
  useEffect(() => {
    if (!registrationData || !registrationID) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsPolling(false);
      return;
    }

    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    const initializeDocuments = async () => {
      const result = await fetchAndPrepareDocuments();
      
      if (result?.needsGeneration && !hasTriggeredGenerationRef.current) {
        console.log('Found templates that need generation, triggering automatically...');
        const triggered = await triggerPDFGenerationForAll();
        if (triggered) {
          hasTriggeredGenerationRef.current = true;
          toast({
            title: "Document Generation Started",
            description: "Review documents are being generated. This page will update automatically.",
            status: "info",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
          setTimeout(() => {
            fetchAndPrepareDocuments(false);
          }, 1000);
        }
      }
      
      if (result?.hasDocuments) {
        const isComplete = await checkGenerationComplete();
        if (isComplete) {
          setIsPolling(false);
          return;
        }
      }
      
      setIsPolling(true);
      setCurrentDocCount(0);
      
      let pollCount = 0;
      const maxPolls = 60;
      let lastDocCount = 0;
      let stableCount = 0;
      
      pollIntervalRef.current = setInterval(async () => {
        pollCount++;
        const result = await fetchAndPrepareDocuments(false);

        const expectedCount = result?.expectedCount || 0;
        const completedCount = result?.doneCount || 0;
        setCurrentDocCount(completedCount);

        if (expectedCount > 0 && completedCount >= expectedCount) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setIsPolling(false);
          return;
        }
        
        if (completedCount === lastDocCount && completedCount > 0) {
          stableCount++;
          if (stableCount >= 3) {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            setIsPolling(false);
            return;
          }
        } else {
          stableCount = 0;
          lastDocCount = completedCount;
        }
        
        if (pollCount >= maxPolls) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setIsPolling(false);
          await fetchAndPrepareDocuments(false);
        }
      }, 3000);
    };
    
    initializeDocuments();
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsPolling(false);
    };
  }, [registrationData, registrationID]);

  // Fetch registration data and check for completion
  const fetchRegistrationAndCheckCompletion = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("registration")
        .select("*")
        .eq("id", registrationID)
        .single();

      if (error) throw error;

      setRegistrationData(data);
      
      console.log(data);

      // Only show inline success if payment is complete, SSG claim submitted, AND signature exists
      if (data.payment_balance === 0 && hasValidRegistrationSignature(data.signature)) {
        if (data.ssg_claim_doc_submitted === true) {
          setShowInlineSuccess(true);
          setShowSkillsFutureResult(false);
          setShowPaymentSelection(false);
        }
      } else {
        // Reset showInlineSuccess if conditions aren't met
        setShowInlineSuccess(false);
      }
    } catch (error) {
      console.error("Error fetching registration:", error);
      toast({
        title: "Error",
        description: "Could not load registration details.",
        status: "error",
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch registration data
  const fetchRegistration = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("registration")
        .select("*, full_certification_runs(*, full_certification(*))")
        .eq("id", registrationID)
        .single();
      if (error) throw error;

      const courseRunCode = data.draft_course_run_code;
      let course_dates = [];
      if (courseRunCode) {
        const { data: sessions, error: sessionsError } = await supabase
          .from("session")
          .select("*")
          .eq("course_run_code", courseRunCode)
          .order("session_date", { ascending: true });

        if (!sessionsError && sessions) {
          course_dates = sessions.map(session => ({
            date: session.session_date,
            start_time: session.start_time ? moment(session.start_time, "HH:mm:ss").format("HHmm") : null,
            end_time: session.end_time ? moment(session.end_time, "HH:mm:ss").format("HHmm") : null,
            room: session.custom_room || session.room_id,
            venue: session.custom_venue || session.venue_id
          }));
        }
      }

      setRegistrationData({
        ...data,
        course_dates: course_dates
      });
      console.log(data);

      // Only show inline success if payment is complete AND signature exists
      // Otherwise, show signature pad if no signature exists
      if (data.payment_balance === 0 && hasValidRegistrationSignature(data.signature)) {
        setShowInlineSuccess(true);
        if (data.ssg_claim_doc_submitted === true) {
          setShowSkillsFutureResult(false);
          setShowPaymentSelection(false);
        }
      } else {
        // Reset showInlineSuccess if conditions aren't met
        setShowInlineSuccess(false);
      }
    } catch (error) {
      console.error("Error fetching registration:", error);
      toast({
        title: "Error",
        description: "Could not load registration details.",
        status: "error",
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  // Send trigger to pipedream
  /* const sendPipedreamTrigger = async (registrationId) => {
    try {
      const response = await fetch('https://eohp67h6n9o17wj.m.pipedream.net', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            object: {
              client_reference_id: registrationId
            }
          }
        }),
      });

      if (response.ok) {
        console.log('Pipedream trigger sent successfully');
        toast({
          title: "Processing Complete",
          description: "Your enrollment has been processed successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      } else {
        throw new Error('Failed to send pipedream trigger');
      }
    } catch (error) {
      console.error('Error sending pipedream trigger:', error);
      toast({
        title: "Processing Error",
        description: "There was an issue processing your enrollment. Please contact support.",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  }; */

  useEffect(() => {
    // Call immediately
    fetchRegistration();

    // Set interval polling
    const interval = setInterval(() => {
      console.log("polling")
      if (hasValidRegistrationSignature(registrationData?.signature)) {
        fetchRegistration();
      }
    }, 10000);

    // Clean up on unmount or when registrationID/toast changes
    return () => clearInterval(interval);
  }, [registrationID, toast]);


  //console.log(registrationData)

  // Check if signature exists and payment balance > 0
  useEffect(() => {
    // if (registrationData && registrationData.signature && registrationData.payment_balance > 0) {
    //   setShowPaymentSelection(true);
    // }
    if (registrationData && hasValidRegistrationSignature(registrationData.signature)) {
      navigate('/student-package/' + registrationID);
    }
  }, [registrationData]);

  // Handle SkillsFuture payment selection
  const handleSkillsFutureSelect = async () => {
    setSkillsFutureStatus({ type: 'info', message: 'Processing your request...' });

    try {
      // Call the relay-ssg function for SkillsFuture
      const payload = {
        webhookData: {
          courseId: registrationData.ssg_course_id,
          courseFee: registrationData.payment_balance,
          startDate: registrationData.start_date,
          nric: registrationData.nric,
          email: registrationData.participant_email,
          homeNumber: registrationData.mobile_no, // You might need to add this field to your query
          mobileNumber: registrationData.mobile_no, // You might need to add this field to your query
          additionalInformation: "registration_id: " + registrationID
        },
        apiType: "Submit Claim",
        timestamp: new Date().toISOString(),
        source: 'sozcode-portal'
      };

      console.log('Sending request to relay-ssg:', payload);

      const { data, error } = await supabase.functions.invoke('relay-ssg', {
        body: payload,
        method: 'POST',
        mode: 'cors'
      });

      if (error) {
        throw new Error(`Relay failed: ${error.message}`);
      }

      console.log('Received response from relay-ssg:', data);

      if (data?.claim_request) {
        setClaimRequest(data.claim_request);
        setShowSkillsFutureForm(true);
        setSkillsFutureStatus({ type: 'success', message: 'Request processed successfully! Please review and submit to continue to SkillsFuture.' });
        
        toast({
          title: "Success",
          description: "Your claim request has been processed. Please submit the form below to continue to SkillsFuture.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error('No claim_request received from relay-ssg');
      }
    } catch (error) {
      console.error('SkillsFuture error:', error);
      
      setSkillsFutureStatus({ type: 'error', message: `Error: ${error.message}` });
      
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle SkillsFuture form submission
  const handleSkillsFutureSubmit = (e) => {
    e.preventDefault();
    
    setSkillsFutureSubmitting(true);
    
    // Create and submit the form to SkillsFuture
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = 'https://sfc.myskillsfuture.gov.sg/sfc2-ind/api/individual/sfcpayment/claim/submit/gateway';

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'encryptedPayload';
    input.value = claimRequest;

    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  // Handle Credit Card / PayNow payment selection
  const handleCreditCardSelect = async () => {
    try {
      toast({
        title: "Redirecting to payment gateway",
        description: "Please wait as we redirect you to the payment page",
        status: "info",
        isClosable: true,
        position: "top",
      });

      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          product_id: registrationData.code,
          registration_id: registrationID,
          participant_email: registrationData.participant_email,
          birth_age: registrationData.birth_age,
          nationality: registrationData.nationality,
          payment_balance: registrationData.payment_balance,
          course_name: registrationData.name,
          course_start_date: moment(registrationData.start_date).format("DD MMM YYYY")
        },
      });

      if (error) {
        throw new Error(`Failed to create checkout session: ${error.message}`);
      }

      if (!data?.url) {
        throw new Error("Checkout URL not found in the response.");
      }

      // Track begin_checkout event in Google Analytics
      if (window.gtag) {
        window.gtag('event', 'begin_checkout', {
          value: registrationData.payment_balance,
          currency: 'SGD',
          items: [{
            item_id: registrationData.code,
            item_name: registrationData.name,
            price: registrationData.payment_balance
          }]
        });
      }

      // Track InitiateCheckout event in Meta Pixel
      if (window.fbq) {
        window.fbq('track', 'InitiateCheckout', {
          value: registrationData.payment_balance,
          currency: 'SGD'
        });
      }

      // Redirect to checkout page
      window.location.href = data.url;
    } catch (error) {
      console.error('Credit card payment error:', error);
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  };

  // Save Signature and Proceed
  const saveSignatureAndProceed = async () => {
    if (isSignatureEmpty) {
      toast({
        title: "Error",
        description: "Please sign the contract before proceeding.",
        status: "error",
        isClosable: true,
        position: "top",
      });
      return;
    }
  
    setSubmitting(true);
    try {
      // Save signature
      const signature = sigPad.current.getTrimmedCanvas().toDataURL("image/png");
      if (!hasValidRegistrationSignature(signature)) {
        toast({
          title: "Error",
          description: "Please draw your signature before proceeding.",
          status: "error",
          isClosable: true,
          position: "top",
        });
        return;
      }

      // Calculate SGT signed datetime for additional_data
      const sgtTime = moment().utcOffset(8).format("YYYY-MM-DD HH:mm:ss");
      const signedAtText = `Signed at:\n${sgtTime}`;
      
      // Prepare updated additional_data
      const existingAdditionalData = registrationData.additional_data || {};
      const updatedAdditionalData = {
        ...existingAdditionalData,
        enrollment_document_signed_at: signedAtText
      };

      // If this registration is part of a full certification, update all rows for the
      // same participant and same certification run. Otherwise, update only this row.
      let updateError = null;
      if (registrationData?.full_certification_id && registrationData?.certification_run_id) {
        const { error } = await supabase
          .from('registration')
          .update({ 
            signature, 
            date_signed: new Date(),
            additional_data: updatedAdditionalData
          })
          .match({
            participant_email: registrationData.participant_email || null,
            full_certification_id: registrationData.full_certification_id,
            certification_run_id: registrationData.certification_run_id
          });
        updateError = error;
      } else {
        const { error } = await supabase
          .from('registration')
          .update({ 
            signature, 
            date_signed: new Date(),
            additional_data: updatedAdditionalData
          })
          .eq('id', registrationID);
        updateError = error;
      }

      if (updateError) {
        throw new Error("Failed to update signature in Supabase");
      }

      // Automatically trigger document regeneration with the new signature
      console.log("Triggering document regeneration with signature...");
      triggerPDFGenerationForAll();

      // Check if this is a full certification course
      const isFullCertification = registrationData?.full_certification_runs;
      
      if (isFullCertification) {
        // toast({
        //   title: "Signature Saved",
        //   description: "Your signature has been saved successfully. The signed student contract will be sent to your email.",
        //   status: "success",
        //   isClosable: true,
        //   position: "top",
        // });

        // fetch('https://eoj5hzqkqmnzf55.m.pipedream.net', {
        //   method: 'POST',
        //   body: JSON.stringify({
        //     email: registrationData.participant_email,
        //     participant_name: registrationData.participant_name,
        //     registration_id: registrationID,
        //   }),
        // });

        // Show thank you page for full certification
        setShowThankYouPage(true);
      } else {
        toast({
          title: "Signature Saved",
          description: "Your signature has been saved successfully. Please select your payment method.",
          status: "success",
          isClosable: true,
          position: "top",
        });
        
        // Show payment method selection for individual courses
        setShowPaymentSelection(true);
      }
  
    } catch (error) {
      console.error("Error during signature save:", {
        message: error.message,
        stack: error.stack,
      });
  
      toast({
        title: "Error during process",
        description: "Failed to save signature. Please try again.",
        status: "error",
        duration: 9000,
        isClosable: true,
        position: "top",
      });

      // Add a second toast with more specific error information if available
      if (error.message.includes("Failed to update signature")) {
        toast({
          title: "Signature Error",
          description: "Could not save signature to Supabase. Please try again.",
          status: "error",
          duration: 9000,
          isClosable: true,
          position: "top",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Start Stripe Checkout
  const startStripeCheckout = async () => {
    try {
      toast({
        title: "Redirecting to Stripe payment page",
        description: "Please wait as we direct you to the payment page",
        status: "info",
        isClosable: true,
        position: "top",
      });

      // Call the Supabase Edge Function with detailed logging
      console.log('Sending request to Edge Function with data:', {
        product_id: registrationData.code,
        registration_id: registrationID,
        participant_email: registrationData.participant_email,
        birth_age: registrationData.birth_age,
        nationality: registrationData.nationality,
        course_name: registrationData.name,
        course_start_date: moment(registrationData.start_date).format("DD MMM YYYY")
      });

      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          product_id: registrationData.code,
          registration_id: registrationID,
          participant_email: registrationData.participant_email,
          birth_age: registrationData.birth_age,
          nationality: registrationData.nationality,
          payment_balance: registrationData.payment_balance,
          course_name: registrationData.name,
          course_start_date: moment(registrationData.start_date).format("DD MMM YYYY")
        },
      });

      // Log the response
      console.log('Edge Function response:', { data, error });

      if (error) {
        console.error('Detailed error information:', error);
        throw new Error(`Failed to create Stripe checkout session: ${error.message}`);
      }

      if (!data?.url) {
        console.error('Invalid response format:', data);
        throw new Error("Stripe checkout URL not found in the response.");
      }

      // Track begin_checkout event in Google Analytics
      if (window.gtag) {
        window.gtag('event', 'begin_checkout', {
          value: registrationData.payment_balance,
          currency: 'SGD',
          items: [{
            item_id: registrationData.code,
            item_name: registrationData.name,
            price: registrationData.payment_balance
          }]
        });
        console.log('Google Analytics begin_checkout event tracked');
      }

      // Track InitiateCheckout event in Meta Pixel
      if (window.fbq) {
        window.fbq('track', 'InitiateCheckout', {
          value: registrationData.payment_balance,
          currency: 'SGD'
        });
        console.log('Meta Pixel InitiateCheckout event tracked');
      }

      // Redirect to Stripe checkout page
      window.location.href = data.url;

    } catch (error) {
      console.error("Error during process:", {
        message: error.message,
        stack: error.stack,
        requestData: {
          product_id: registrationData.code,
          registration_id: registrationID,
          participant_email: registrationData.participant_email,
          birth_age: registrationData.birth_age,
          nationality: registrationData.nationality,
          course_name: registrationData.name,
          course_start_date: moment(registrationData.start_date).format("DD MMM YYYY")
        }
      });

      // Keep existing toast for user feedback
      toast({
        title: "Error during process",
        description: "- Error: Failed to create Stripe checkout session",
        status: "error",
        duration: 9000,
        isClosable: true,
        position: "top",
      });

    } finally {
      setSubmitting(false);
    }
  };


  // Clear Signature
  const clearSignature = () => {
    sigPad.current.clear();
    setIsSignatureEmpty(true);
  };

  if (loading) {
    return (
      <Center h="100vh" bg="gray.50">
        <Spinner size="xl" thickness="4px" speed="0.65s" color="brand.600" />
      </Center>
    );
  }

  if (!registrationData) {
    return (
      <Center h="100vh" bg="gray.50">
        <Text>No registration data found.</Text>
      </Center>
    );
  }

  // Show inline success message if enrollment is complete
  if (showInlineSuccess) {
    return (
      <Box
        w={"100%"} 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        bg="gray.50" 
        minH="100vh" 
        py={8}
      >
        <Box 
          width="100%" 
          bg="white" 
          px={10}
          py={10}
          borderRadius="lg" 
          boxShadow="md"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <VStack spacing={8} w="full">
              <motion.div
                initial={{ y: -50 }}
                animate={{ y: 0 }}
                transition={{ delay: 0.2, duration: 0.8, ease: "easeOut" }}
              >
                <Heading 
                  size={"sm"} 
                  color="brand.700"
                  borderBottom="2px solid"
                  borderColor="green.600"
                  pb={2}
                >
                  🎉 Enrollment Complete!
                </Heading>
              </motion.div>

              <motion.div
                initial={{ x: 100 }}
                animate={{ x: 0 }}
                transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
              >
                <Text fontSize="md" color="brand.700">
                  Thank you for your payment, <strong>{registrationData?.participant_name}</strong>. Your enrollment for this course is confirmed.
                </Text>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 1, ease: "easeOut" }}
              >
                <Text fontSize="md" color="brand.700">
                  You will receive the signed student contract via your email <Text as="span" color="orange.500" fontWeight="bold">{registrationData.participant_email}</Text> soon.
                </Text>
                <br/>
                <Text fontSize="md" fontWeight="bold" color="brand.700" textAlign="center">
                  See you for the course!
                </Text>
              </motion.div>

              {/* Course Details Section */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 1, ease: "easeOut" }}
              >
                <Box
                  bg="gray.50"
                  border="1px solid"
                  borderColor="brand.300"
                  borderRadius="md"
                  p={6}
                  boxShadow="sm"
                >
                  <Text fontSize="lg" fontWeight="bold" mb={4} color="brand.700">
                    {registrationData?.name}
                  </Text>
                  <Table variant="simple" size="sm" width="100%" bg="white">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th textAlign="center" border="1px solid" borderColor="brand.300" bg="gray.50" color="brand.700">Date</Th>
                        <Th textAlign="center" border="1px solid" borderColor="brand.300" bg="gray.50" color="brand.700">Start Time</Th>
                        <Th textAlign="center" border="1px solid" borderColor="brand.300" bg="gray.50" color="brand.700">End Time</Th>
                      </Tr>
                    </Thead>
                    <Tbody bg="white">
                      {registrationData?.course_dates.map((session, index) => (
                        <Tr key={index} bg="white">
                          <Td textAlign="center" border="1px solid" borderColor="brand.300" bg="white" color="brand.700">
                            {moment(session.date).format("DD MMM YYYY (ddd)")}
                          </Td>
                          <Td textAlign="center" border="1px solid" borderColor="brand.300" bg="white" color="brand.700">
                            {moment(session.start_time, "HHmm").format("h:mm A")}
                          </Td>
                          <Td textAlign="center" border="1px solid" borderColor="brand.300" bg="white" color="brand.700">
                            {moment(session.end_time, "HHmm").format("h:mm A")}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  <Text mt={4} textAlign="left" color="brand.700">
                    <b>Venue:</b> Habitat Learning Centre, 1 Goldhill Plaza, #02-27, Singapore 308899. The nearest MRT is Novena.
                  </Text>
                  <Text mt={4} textAlign="left" color="brand.700">
                    <b>Directions:</b>{" "}
                    <a
                      href="https://sozcode.com/goldhill-plaza-how-to-get-there"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "orange.500", textDecoration: "underline" }}
                    >
                      https://sozcode.com/goldhill-plaza-how-to-get-there
                    </a>
                  </Text>
                  <Text mt={4} textAlign="left" color="brand.700">
                    <b>Duration:</b> {registrationData?.course_duration} hours
                  </Text>
                </Box>
              </motion.div>
            </VStack>
          </motion.div>
        </Box>
      </Box>
    );
  }

  // Show SkillsFuture form if claim request is ready
  if (showSkillsFutureForm) {
    return (
      <Container 
        w={"100%"} 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        bg="gray.50" 
        minH="100vh" 
        py={8}
        px={{ base: 4, md: 8 }}
      >
        <Box 
          width="100%" 
          bg="white" 
          maxW={{ base: "100%", md: "800px" }} 
          px={{ base: 4, md: 8 }} 
          py={{ base: 6, md: 8 }} 
          borderRadius="lg" 
          boxShadow="md"
        >
          <Heading 
            size={{ base: "sm", md: "md" }}
            mb={{ base: 6, md: 8 }} 
            color="brand.700" 
            borderBottom="2px solid" 
            borderColor="brand.400" 
            pb={2} 
            width="fit-content"
          >
            SkillsFuture Credit Pay Claim
          </Heading>

          <Box>
            <Alert status="info" mb={6} borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Ready to Submit to SkillsFuture</Text>
                <Text fontSize="sm">
                  Your claim request has been processed successfully. When you click submit below, 
                  you will be redirected to the SkillsFuture website to make payment under your SkillsFuture credits.
                </Text>
              </Box>
            </Alert>

            {skillsFutureStatus && (
              <Alert status={skillsFutureStatus.type} mb={6} borderRadius="md">
                <AlertIcon />
                {skillsFutureStatus.message}
              </Alert>
            )}

            <form onSubmit={handleSkillsFutureSubmit}>
              <VStack spacing={6} align="stretch">
                <Box>
                  <Text fontWeight="bold" color="brand.700" mb={2}>
                    Instructions:
                  </Text>
                  <Text color="brand.700" mb={4}>
                    Click the submit button below to be redirected to the SkillsFuture website where you can 
                    complete your payment using your SkillsFuture credits. Your claim data has been securely 
                    prepared and will be automatically submitted to SkillsFuture.
                  </Text>
                </Box>

                {/* Hidden input with the claim request */}
                <input
                  type="hidden"
                  name="encryptedPayload"
                  value={claimRequest}
                />

                <Button
                  type="submit"
                  bg="brand.600"
                  color="white"
                  size="lg"
                  _hover={{ bg: 'brand.700' }}
                  isLoading={skillsFutureSubmitting}
                  loadingText="Submitting..."
                >
                  Continue to SkillsFuture
                </Button>

                <Button
                  variant="outline"
                  color="brand.700"
                  borderColor="brand.700"
                  onClick={() => {
                    setShowSkillsFutureForm(false);
                    setClaimRequest("");
                    setSkillsFutureStatus(null);
                  }}
                  _hover={{ bg: 'brand.700', color: "white" }}
                >
                  Back to Payment Selection
                </Button>
              </VStack>
            </form>
          </Box>
        </Box>
      </Container>
    );
  }

  // Show SkillsFuture result if available
  if (showSkillsFutureResult) {
    return (
      <Container 
        w={"100%"} 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minH="100vh" 
        py={8}
        px={{ base: 4, md: 8 }}
      >
        <Box 
          width="100%" 
          bg="white" 
          maxW={{ base: "100%", md: "800px" }} 
          px={{ base: 4, md: 8 }} 
          py={{ base: 6, md: 8 }} 
          borderRadius="lg" 
          boxShadow="md"
        >
          <Heading 
            size={{ base: "sm", md: "md" }}
            mb={{ base: 6, md: 8 }} 
            borderBottom="2px solid" 
            pb={2} 
            width="fit-content"
          >
            SkillsFuture Claim Result
          </Heading>

          <Box>
            <Alert 
              status={skillsFutureResult.type} 
              mb={6} 
              borderRadius="md"
            >
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">
                  {skillsFutureResult.type === 'success' ? 'Claim Successful!' : 'Claim Failed'}
                </Text>
                <Text fontSize="sm">
                  {skillsFutureResult.message}
                </Text>
                {skillsFutureResult.type === 'success' && skillsFutureResult.claimedAmount > 0 && (
                  <Text fontSize="sm" mt={2}>
                    Claim ID: {skillsFutureResult.claimId}
                  </Text>
                )}
              </Box>
            </Alert>

            <VStack spacing={6} align="stretch">
              {skillsFutureResult.type === 'success' && registrationData && (
                <Box>
                  <Text fontWeight="bold" mb={2}>
                    Payment Summary:
                  </Text>
                  <Text>
                    • SkillsFuture Claimed: SGD ${skillsFutureResult.claimedAmount}
                  </Text>
                  <Text>
                    • Remaining Balance: SGD ${registrationData.payment_balance}
                  </Text>
                </Box>
              )}

              <VStack spacing={4}>
                {skillsFutureResult.type === 'success' && registrationData && registrationData.payment_balance > 0 && (
                  <Button
                    bg="brand.600"
                    color="white"
                    size="lg"
                    onClick={handleCreditCardSelect}
                    _hover={{ bg: '#e06835' }}
                  >
                    Proceed to Pay Remaining Balance
                  </Button>
                )}

                {skillsFutureResult.type === 'success' && registrationData && registrationData.payment_balance <= 0 && (
                  <Button
                    bg="brand.600"
                    color="white"
                    size="lg"
                    onClick={() => navigate('/success')}
                    _hover={{ bg: '#4a8f6b' }}
                  >
                    Payment Complete - View Course Details
                  </Button>
                )}

                {skillsFutureResult.type === 'error' && (
                  <Button
                    bg="brand.600"
                    color="white"
                    size="lg"
                    onClick={() => {
                      setShowSkillsFutureResult(false);
                      setShowPaymentSelection(true);
                    }}
                    _hover={{ bg: '#e06835' }}
                  >
                    Try Alternative Payment Method
                  </Button>
                )}

                {!paymentLink ? (
                  <Button
                    variant="outline"
                    color="brand.700"
                    borderColor="brand.700"
                    onClick={async () => {
                      try {
                        // Create a Stripe checkout session for sharing
                        const { data, error } = await supabase.functions.invoke("create-checkout-session", {
                          body: {
                            product_id: registrationData.code,
                            registration_id: registrationID,
                            participant_email: registrationData.participant_email,
                            birth_age: registrationData.birth_age,
                            nationality: registrationData.nationality,
                            payment_balance: registrationData.payment_balance,
                            course_name: registrationData.name,
                            course_start_date: moment(registrationData.start_date).format("DD MMM YYYY")
                          },
                        });

                        if (error) throw error;

                        setPaymentLink(data.url);
                        
                        toast({
                          title: "Payment Link Created!",
                          description: "Your payment link is ready. You can copy it or share it with others.",
                          status: "success",
                          duration: 3000,
                          isClosable: true,
                          position: "top",
                        });
                      } catch (error) {
                        console.error('Error creating payment link:', error);
                        toast({
                          title: "Error",
                          description: "Failed to create payment link. Please try again.",
                          status: "error",
                          duration: 3000,
                          isClosable: true,
                          position: "top",
                        });
                      }
                    }}
                    _hover={{ bg: "brand.700", color: "white" }}
                  >
                    Create Payment Link
                  </Button>
                ) : (
                  <VStack spacing={3} width="100%">
                    <Box 
                      p={3} 
                      border="1px solid" 
                      borderRadius="md"
                      bg="gray.50"
                      width="100%"
                    >
                      <Text fontSize="sm" color="brand.700" fontWeight="bold" mb={2}>
                        Payment Link:
                      </Text>
                      <Text fontSize="xs" color="brand.700" wordBreak="break-all">
                        {paymentLink}
                      </Text>
                    </Box>
                    <HStack spacing={2} width="100%">
                      <Button
                        size="sm"
                        bg="brand.600"
                        color="white"
                        onClick={() => window.open(paymentLink, '_blank')}
                        _hover={{ bg: '#e06835' }}
                        flex={1}
                      >
                        Open Payment Page
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        color="brand.700"
                        borderColor="brand.700"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(paymentLink);
                            toast({
                              title: "Link Copied!",
                              description: "Payment link copied to clipboard.",
                              status: "success",
                              duration: 2000,
                              isClosable: true,
                              position: "top",
                            });
                          } catch (error) {
                            toast({
                              title: "Error",
                              description: "Failed to copy link.",
                              status: "error",
                              duration: 2000,
                              isClosable: true,
                              position: "top",
                            });
                          }
                        }}
                        _hover={{ bg: "brand.700", color: "white" }}
                      >
                        Copy
                      </Button>
                    </HStack>
                  </VStack>
                )}
              </VStack>
            </VStack>
          </Box>
        </Box>
      </Container>
    );
  }

  // Show PaymentMethodSelection if signature exists and payment balance > 0
  // Show thank you page for full certification courses after signing
  if (showThankYouPage) {
    return (
      <Container 
        w={"100%"} 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minH="100vh"
        bg="gray.50"
        py={8}
      >
        <Box
          maxW="600px"
          w="100%"
          bg="white"
          borderRadius="xl"
          boxShadow="xl"
          p={8}
          textAlign="center"
        >
          <Box mb={6}>
            <Icon as={CheckCircleIcon} w={16} h={16} color="green.500" mb={4} />
            <Heading size="lg" color="green.600" mb={4}>
              Thank You!
            </Heading>
            <Text fontSize="lg" color="gray.600" mb={6}>
              Your signature has been successfully saved. The signed documents will be sent to your email at <strong>{registrationData.participant_email}</strong>.
            </Text>
            <Text fontSize="md" color="gray.500">
              Please check your email for the contract and keep it for your records.
            </Text>
          </Box>
          
          {/* <Button
            colorScheme="green"
            size="lg"
            onClick={() => navigate('/')}
            w="full"
            maxW="300px"
          >
            Return to Home
          </Button> */}
        </Box>
      </Container>
    );
  }

  if (showPaymentSelection) {
    return (
      <PaymentMethodSelection
        registrationData={registrationData}
        registrationID={registrationID}
        onSkillsFutureSelect={handleSkillsFutureSelect}
        onCreditCardSelect={handleCreditCardSelect}
        paymentBalance={registrationData.payment_balance}
      />
    );
  }

  if (!hasValidRegistrationSignature(registrationData.signature)) {
    return (
      <>
        <Container
            w={"100%"}
            maxW="1000px"
            display="flex"
            justifyContent="center"
            alignItems="center"
            bg="gray.50"
            minH="100vh"
            py={{ base: 4, md: 8 }}
            px={{ base: 2, md: 4 }}
        >
          <Box
              width="100%"
              bg="white"
              px={{ base: 4, md: 10 }}
              py={{ base: 6, md: 10 }}
              borderRadius="xl"
              boxShadow="xl"
          >
            <VStack align="start" spacing={6} w="full">
              <Box w="full">
                <Heading
                    size="md"
                    mb={2}
                    color="brand.700"
                    pb={2}
                >
                  Enrollment Review & Signature
                </Heading>
                <Divider borderColor="brand.400" borderBottomWidth="2px" w="100px" />
              </Box>

              {/* Welcome Alert */}
              <Alert 
                status="success" 
                borderRadius="lg" 
                bg="blue.50" 
                border="1px solid" 
                borderColor="blue.200"
              >
                <AlertIcon color="blue.500" />
                <Box>
                  <Text fontWeight="bold" color="blue.800">Welcome, {registrationData.participant_name}!</Text>
                  <Text fontSize="sm" color="blue.700">
                    We're excited to have you join us. Please review the enrollment documents below and provide your signature at the bottom to complete your registration.
                  </Text>
                </Box>
              </Alert>

              {paymentSuccess && (
                <Alert status="success" borderRadius="lg" mb={4}>
                  <AlertIcon/>
                  <Box>
                    <Text fontWeight="bold">Payment Received</Text>
                    <Text fontSize="sm">Your payment has been successfully processed. Please review the documents below and sign to complete your enrollment.</Text>
                  </Box>
                </Alert>
              )}

              <Alert status="info" borderRadius="lg" variant="left-accent">
                <AlertIcon/>
                <Text fontSize="sm">
                  Please review each document carefully. You can view or print them for your records.
                </Text>
              </Alert>

              {/* Documents Section Header with Polling Status */}
              <Box w="full">
                <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} mb={4} flexDirection={{ base: "column", md: "row" }} gap={3}>
                  <Heading size="sm" color="brand.700" display="flex" alignItems="center" gap={2}>
                    <Icon as={FaFileAlt} />
                    Documents to Review
                  </Heading>
                  {isPolling && (
                    <Badge colorScheme="blue" variant="outline" p={2} borderRadius="md" display="flex" alignItems="center" gap={2}>
                      <Spinner size="xs" />
                      Preparing documents... {currentDocCount}/{documents.length} ready
                    </Badge>
                  )}
                </Flex>
                
                <VStack spacing={3} align="stretch">
                  {documents.length > 0 ? (
                    documents.map((doc, index) => {
                      const isDone = doc.status === 'done';
                      const isGenerating = doc.status === 'generating' || doc.status === 'not_started';

                      return (
                        <Flex
                          key={index}
                          align={{ base: "flex-start", md: "center" }}
                          p={{ base: 3, md: 4 }}
                          border="1px solid"
                          borderColor={isDone ? "green.200" : "yellow.200"}
                          borderRadius="md"
                          bg={isDone ? "white" : "yellow.50"}
                          justify="space-between"
                          _hover={isDone ? { bg: "gray.50", borderColor: "brand.400" } : {}}
                          transition="all 0.2s"
                          flexDirection={{ base: "column", md: "row" }}
                          gap={{ base: 3, md: 0 }}
                        >
                          <HStack spacing={3} flex="1" mr={{ base: 0, md: 4 }} w={{ base: "100%", md: "auto" }}>
                            {isDone ? (
                              <Box color="green.500" fontSize={{ base: "20px", md: "24px" }}>
                                <CheckCircleIcon />
                              </Box>
                            ) : (
                              <Spinner size="sm" color="yellow.500" />
                            )}
                            <VStack align="start" spacing={0} flex={1} minW={0}>
                              <HStack spacing={2} flexWrap="wrap">
                                <Text fontWeight="semibold" fontSize={{ base: "sm", md: "md" }} color={isDone ? "gray.900" : "gray.700"} noOfLines={2}>
                                  {getDisplayDocumentName(doc.title)}
                                </Text>
                                <Badge colorScheme={isDone ? "green" : "yellow"} size={{ base: "xs", md: "sm" }}>
                                  {isDone ? "Ready" : "Generating"}
                                </Badge>
                              </HStack>
                              <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">
                                {isDone ? "Document ready for review" : "Please wait while we prepare this document..."}
                              </Text>
                            </VStack>
                          </HStack>
                          <HStack spacing={2} w={{ base: "100%", md: "auto" }} justify={{ base: "flex-end", md: "auto" }}>
                            <Button
                              size={{ base: "xs", md: "sm" }}
                              variant="outline"
                              leftIcon={<ViewIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                viewDocument(doc);
                              }}
                              isDisabled={!isDone}
                              flex={{ base: 1, md: "none" }}
                            >
                              View
                            </Button>
                            <Button
                              size={{ base: "xs", md: "sm" }}
                              colorScheme="blue"
                              leftIcon={<DownloadIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadDocumentAsPdf(doc);
                              }}
                              isLoading={downloadingDoc === doc.title}
                              isDisabled={!isDone}
                              flex={{ base: 1, md: "none" }}
                            >
                              <Text display={{ base: "none", sm: "block" }}>Download PDF</Text>
                              <Text display={{ base: "block", sm: "none" }}>Download</Text>
                            </Button>
                          </HStack>
                        </Flex>
                      );
                    })
                  ) : (
                    <Box p={8} textAlign="center" border="2px dashed" borderColor="gray.200" borderRadius="lg">
                      <Spinner size="sm" mr={2} />
                      <Text color="gray.500" display="inline">Connecting to document generator...</Text>
                    </Box>
                  )}
                </VStack>
              </Box>

              {/* Signature Area - Improved UI/UX */}
              <Box 
                w="full" 
                p={{ base: 4, md: 8 }} 
                bg="gray.50" 
                borderRadius="xl" 
                border="1px solid" 
                borderColor="gray.200"
                boxShadow="inner"
              >
                <VStack spacing={6} align="stretch">
                  <Box textAlign="center">
                    <Heading size="sm" color="brand.700" mb={1} display="flex" alignItems="center" justifyContent="center" gap={2}>
                      <Icon as={FaSignature} />
                      Digital Signature
                    </Heading>
                    <Text fontSize="xs" color="gray.500">
                      Please use your mouse or touch screen to sign inside the box below
                    </Text>
                  </Box>

                  <Box
                    bg="white"
                    borderRadius="lg"
                    border="2px solid"
                    borderColor={isSignatureEmpty ? "gray.300" : "brand.400"}
                    position="relative"
                    overflow="hidden"
                    boxShadow="sm"
                    transition="border-color 0.2s"
                  >
                    <AspectRatio ratio={21/9} w="full">
                      <SignaturePad
                        ref={sigPad}
                        canvasProps={{
                          className: "signatureCanvas",
                          style: {
                            width: '100%',
                            height: '100%',
                            cursor: 'crosshair'
                          }
                        }}
                        onEnd={() => {
                          setIsSignatureEmpty(sigPad.current?.isEmpty());
                        }}
                      />
                    </AspectRatio>
                    
                    {isSignatureEmpty && (
                      <Center 
                        position="absolute" 
                        top={0} 
                        left={0} 
                        right={0} 
                        bottom={0} 
                        pointerEvents="none"
                        opacity={0.4}
                      >
                        <VStack spacing={1}>
                          <Icon as={EditIcon} boxSize={6} color="gray.400" />
                          <Text fontSize="xs" color="gray.400" fontWeight="medium">SIGN HERE</Text>
                        </VStack>
                      </Center>
                    )}
                  </Box>

                  <Flex justify="space-between" align="center" wrap="wrap" gap={6}>
                    <VStack align="start" spacing={1} flex={1} minW="200px">
                      <HStack>
                        <Icon as={FaUser} color="gray.400" boxSize={3} />
                        <Text fontSize="xs" fontWeight="bold" color="gray.600">SIGNED BY:</Text>
                      </HStack>
                      <Text fontSize="sm" color="gray.800" fontWeight="semibold">
                        {registrationData.participant_name}
                      </Text>
                      <HStack>
                        <Icon as={FaCalendarAlt} color="gray.400" boxSize={3} />
                        <Text fontSize="xs" color="gray.500">
                          Date: {moment().format("DD MMM YYYY")}
                        </Text>
                      </HStack>
                    </VStack>

                    <Flex 
                      direction={{ base: "column", sm: "row" }} 
                      w={{ base: "100%", md: "auto" }} 
                      gap={3}
                    >
                      <Button
                        size="md"
                        variant="outline"
                        leftIcon={<DeleteIcon />}
                        onClick={clearSignature}
                        isDisabled={isSignatureEmpty && !submitting}
                        w={{ base: "100%", sm: "auto" }}
                      >
                        Clear
                      </Button>
                      <Button
                        colorScheme="brand"
                        size="lg"
                        px={8}
                        onClick={saveSignatureAndProceed}
                        isLoading={submitting}
                        isDisabled={isSignatureEmpty}
                        loadingText="Saving..."
                        boxShadow="md"
                        w={{ base: "100%", sm: "auto" }}
                        _hover={{ transform: "translateY(-1px)", boxShadow: "lg" }}
                        _active={{ transform: "translateY(0)" }}
                      >
                        Accept & Sign Contract
                      </Button>
                    </Flex>
                  </Flex>
                  
                  <Box p={3} bg="orange.50" borderRadius="md" borderLeft="4px solid" borderColor="orange.400">
                    <HStack align="start">
                      <Icon as={FaInfoCircle} color="orange.500" mt={1} />
                      <Text fontSize="2xs" color="orange.800">
                        By signing, you acknowledge that you have read and understood the enrollment documents and agree to the terms and conditions stated therein.
                      </Text>
                    </HStack>
                  </Box>
                </VStack>
              </Box>
            </VStack>
          </Box>
        </Container>

        {/* Document Viewer Modal - Improved desktop layout */}
        <Modal 
          isOpen={isDocOpen} 
          onClose={onDocClose} 
          size={{ base: "full", md: "6xl" }} 
          scrollBehavior="inside"
          isCentered={{ base: false, md: true }}
        >
          <ModalOverlay />
          <ModalContent 
            maxW={{ base: "100vw", md: "90vw" }} 
            h={{ base: "100vh", md: "90vh" }} 
            maxH={{ base: "100vh", md: "90vh" }} 
            m={{ base: 0, md: "auto" }} 
            borderRadius={{ base: 0, md: "xl" }} 
            display="flex" 
            flexDirection="column"
            overflow="hidden"
          >
            <ModalHeader color="brand.700" borderBottom="2px solid" borderColor="brand.300" fontSize={{ base: "sm", md: "md" }} py={{ base: 2, md: 4 }} px={{ base: 3, md: 6 }} flexShrink={0}>
              {getDisplayDocumentName(activeDocument?.title) || "Document"}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody p={0} flex="1" overflow="hidden" display="flex" flexDirection="column" bg="gray.100">
              {activeDocument && activeDocument.isPdf && activeDocument.pdfFileId ? (
                <Box
                  p={0}
                  flex="1"
                  display="flex"
                  flexDirection="column"
                  minH={0}
                  w="full"
                >
                  {/* Display PDF using Google Drive viewer in iframe exactly like StudentPackage.js */}
                  <iframe
                    src={`https://drive.google.com/file/d/${activeDocument.pdfFileId}/preview`}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      flex: 1,
                    }}
                    title={activeDocument.title}
                  />
                </Box>
              ) : (
                <Box p={{ base: 4, md: 6 }} textAlign="center" my="auto">
                  <VStack spacing={4}>
                    <Spinner size="xl" color="brand.500" thickness="4px" />
                    <Text color="gray.600" fontSize={{ base: "sm", md: "md" }} fontWeight="medium">
                      {activeDocument?.status === 'generating' 
                        ? 'Document is being prepared. Please wait...'
                        : activeDocument?.status === 'not_started'
                        ? 'Document generation started. Please wait...'
                        : 'Connecting to document service...'}
                    </Text>
                  </VStack>
                </Box>
              )}
            </ModalBody>
            <ModalFooter flexDirection={{ base: "column", md: "row" }} gap={2} py={{ base: 2, md: 4 }} px={{ base: 3, md: 6 }} flexShrink={0} borderTop="1px solid" borderColor="gray.100" bg="white">
              <Button colorScheme="brand" variant="outline" onClick={onDocClose} w={{ base: "100%", md: "auto" }} order={{ base: 2, md: 1 }} size={{ base: "sm", md: "md" }}>
                Close
              </Button>
              {activeDocument && (
                <Button
                  colorScheme="blue"
                  leftIcon={<DownloadIcon />}
                  onClick={() => downloadDocumentAsPdf(activeDocument)}
                  isLoading={downloadingDoc === activeDocument.title}
                  isDisabled={!activeDocument.isPdf || !activeDocument.pdfFileId}
                  w={{ base: "100%", md: "auto" }}
                  order={{ base: 1, md: 2 }}
                  size={{ base: "sm", md: "md" }}
                  boxShadow="sm"
                >
                  Download PDF
                </Button>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return null;
};
